/**
 * BriefSectionCard — preview of a single brief section.
 *
 * States:
 *   - populated: shows summarized lines + "Pin to chat" toggle
 *   - empty: renders SectionGhostPlaceholder with section-specific micro-copy
 *
 * The card is the single source of truth for "what the brief currently
 * says".  PatchCard diffs in the chat are the audit trail.
 */

import { SectionGhostPlaceholder, type SectionKey } from './SectionGhostPlaceholder';

export type { SectionKey };

export const SECTION_ORDER: Array<{ key: SectionKey; label: string; tint: string }> = [
  { key: 'objective', label: 'Objective', tint: 'from-sky-50 to-white border-sky-200' },
  { key: 'audience', label: 'Audience', tint: 'from-violet-50 to-white border-violet-200' },
  { key: 'insight', label: 'Insight', tint: 'from-amber-50 to-white border-amber-200' },
  { key: 'brand_voice', label: 'Brand Voice', tint: 'from-teal-50 to-white border-teal-200' },
  { key: 'message', label: 'Message', tint: 'from-rose-50 to-white border-rose-200' },
  { key: 'creative_context', label: 'Creative Context', tint: 'from-indigo-50 to-white border-indigo-200' },
  { key: 'mandatories', label: 'Mandatories', tint: 'from-orange-50 to-white border-orange-200' },
  { key: 'success_metrics', label: 'Success Metrics', tint: 'from-emerald-50 to-white border-emerald-200' },
];

interface BriefSectionCardProps {
  sectionKey: SectionKey;
  label: string;
  tint: string;
  data: Record<string, unknown> | null;
  isPinned: boolean;
  wasPatched: boolean;
  onTogglePin?: () => void;
  draftValue?: string;
  onDraftSection: () => void;
  onQuickApply?: () => Promise<void>;
  canInteract: boolean;
}

export function BriefSectionCard({
  sectionKey,
  label,
  tint,
  data,
  isPinned,
  wasPatched,
  onTogglePin,
  draftValue,
  onDraftSection,
  onQuickApply,
  canInteract,
}: BriefSectionCardProps) {
  const preview = summarize(sectionKey, data);
  return (
    <article
      className={`relative rounded-2xl border bg-gradient-to-br ${tint} p-4 transition ${
        wasPatched ? 'ring-2 ring-emerald-300/70 ring-offset-2 ring-offset-slate-50' : ''
      } ${isPinned ? 'ring-2 ring-brand-purple/40 ring-offset-2 ring-offset-slate-50' : ''}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h2 className="text-sm font-bold text-slate-900">{label}</h2>
          {wasPatched && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Just updated
            </span>
          )}
        </div>
        {onTogglePin && (
          <button
            onClick={onTogglePin}
            className={`text-[10px] font-semibold px-2 py-1 rounded border transition ${
              isPinned
                ? 'bg-brand-purple text-white border-brand-purple hover:bg-brand-purple/90'
                : 'bg-white text-slate-600 border-slate-200 hover:border-brand-purple hover:text-brand-purple'
            }`}
          >
            {isPinned ? '✓ Pinned' : 'Edit in chat'}
          </button>
        )}
      </div>
      <div className="text-sm text-slate-700 space-y-1">
        {preview.length === 0 ? (
          <SectionGhostPlaceholder
            sectionKey={sectionKey}
            draftValue={draftValue}
            onDraftSection={onDraftSection}
            onQuickApply={onQuickApply}
            canInteract={canInteract}
          />
        ) : (
          preview.map((line, i) => (
            <p key={i} className="leading-snug">
              {line}
            </p>
          ))
        )}
      </div>
    </article>
  );
}

function summarize(key: SectionKey, data: Record<string, unknown> | null): string[] {
  if (!data || Object.keys(data).length === 0) return [];
  const out: string[] = [];
  const push = (label: string, v: unknown) => {
    if (v == null) return;
    if (typeof v === 'string') {
      if (v.trim()) out.push(`${label}: ${truncate(v)}`);
    } else if (Array.isArray(v)) {
      if (v.length > 0) out.push(`${label}: ${v.slice(0, 3).map(String).join(', ')}${v.length > 3 ? '…' : ''}`);
    } else if (typeof v === 'object') {
      const sv = v as Record<string, unknown>;
      const pick =
        (typeof sv.name === 'string' && sv.name) ||
        (typeof sv.label === 'string' && sv.label) ||
        '';
      if (pick) out.push(`${label}: ${truncate(pick as string)}`);
    }
  };

  if (key === 'objective') {
    push('Statement', data.statement);
    push('Campaign', data.campaign_type);
    push('Funnel', data.funnel_stage);
  } else if (key === 'audience') {
    push('Persona', data.persona_name ?? data.persona_id);
    push('One-liner', data.one_liner);
  } else if (key === 'insight') {
    push('Tension', data.tension);
    push('Timing', data.timing);
  } else if (key === 'brand_voice') {
    push('Tone', data.tone);
    push('Style', data.communication_style);
  } else if (key === 'message') {
    push('Hook', data.hook_direction);
    push('Headlines', data.headlines);
    push('CTA', data.cta_button);
  } else if (key === 'creative_context') {
    push('Visual', data.visual_preferences);
    push('Scenarios', data.scenario_settings);
  } else if (key === 'mandatories') {
    push('Required claims', data.required_claims);
    push('Compliance', data.compliance_notes);
    push('Brand promises', data.brand_promises);
    push('Policy', data.policy_considerations);
  } else if (key === 'success_metrics') {
    push('KPIs', data.primary_kpis);
    push('Secondary KPIs', data.secondary_kpis);
    push('Signals', data.creative_signals);
  }
  return out;
}

function truncate(s: string, max = 180): string {
  return s.length > max ? `${s.slice(0, max - 1).trim()}…` : s;
}
